
/**
 * Information Modal for each board and all the section settings
 *
 * @author Reflect-Media <reflect.media GmbH>
 * @version 0.0.1
 *
 * @todo [ ] Test the component
 * @todo [ ] Integration test.
 * @todo [✔] Update the typescript.
 */

import Vue from "vue";
import { Component, Prop } from "vue-property-decorator";
import { mapGetters, mapActions, mapMutations } from "vuex";

import InformationTab from "@/components/project/SingleProject/Schematic/InformationModal/InformationTab.vue";
import SectionSettingsTab from "@/components/project/SingleProject/Schematic/InformationModal/SectionSettingsTab.vue";
import SectionRawDataTab from "@/components/project/SingleProject/Schematic/InformationModal/SectionRawDataTab.vue";
import AdministrationTab from "@/components/project/SingleProject/Schematic/InformationModal/AdministrationTab.vue";
import MetricsTab from "@/components/project/SingleProject/Schematic/InformationModal/MetricsTab.vue";
import IOTab from "@/components/project/SingleProject/Schematic/InformationModal/IOTab.vue";

import Section from "@/model/ModelSection";
import SectionState from "@/model/ModelSectionState";
import Project from "@/model/Project";
import UserAccess from "@/model/User/UserAccess";
import { ProjectTypeInterfaceInformationModal } from "@/model/MainProject/MainProjectSection";
import VueI18n from "vue-i18n";
import APITab from "@/components/project/SingleProject/Schematic/InformationModal/APITab.vue";

@Component({
  name: "SingleBoardInformationModal",
  methods: {
    ...mapMutations("projects", {
      RESET_INFORMATION_MODAL: "ResetInformationModal",
      RESET_CAMERA_PICTURE: "ResetCameraSectionLatestPicture"
    }),
    ...mapActions("projects", {
      refreshSectionAllSections: "GetAllSectionFromTheProject",
      getSectionDetailsFromBackEnd: "GetSectionDetail",
      GetCurrentSectionState: "GetCurrentSectionState",
      GET_LATEST_COUNTER: "GetTrafficCounter",
      getVwaSectionDetail: "GetVwaSectionDetail",
      getVwaSectionState: "GetVwaSectionState"
    })
  },
  computed: {
    ...mapGetters(["getUserAccess"]),
    ...mapGetters("projects", {
      currentProject: "GetCurrentProject",
      currentSectionState: "GetCurrentSectionState",
      currentSectionDetails: "GetCurrentSectionDetails"
    })
  },
  components: {
    APITab,
    InformationTab,
    SectionSettingsTab,
    SectionRawDataTab,
    AdministrationTab,
    MetricsTab,
    IOTab
  }
})
export default class SingleBoardInformationModal extends Vue {
  $t!: VueI18n["t"];
  /*----------  Component  props section id and the board position ----------*/
  @Prop({ required: true, type: Number }) sectionId!: number;
  @Prop({ required: true, type: String }) boardPosition!: string;
  @Prop({ type: Boolean, default: false }) isDashboardModal!: boolean;
  @Prop({ type: Boolean, default: false }) trafficReport!: boolean;
  @Prop({ type: Object as () => ProjectTypeInterfaceInformationModal })
  projectType!: ProjectTypeInterfaceInformationModal;
  /** VWA device page: only the settings tab, loaded through the VWA endpoints */
  @Prop({ type: Boolean, default: false }) vwaDeviceMode!: boolean;

  /*----------  Vuex   ----------*/
  private GetCurrentSectionState!: Function;
  private getVwaSectionDetail!: Function;
  private getVwaSectionState!: Function;
  private getSectionDetailsFromBackEnd!: Function;
  private refreshSectionAllSections!: Function;
  private RESET_INFORMATION_MODAL!: Function;
  private RESET_CAMERA_PICTURE!: Function;
  private GET_LATEST_COUNTER!: Function;

  private readonly currentProject!: Project;
  readonly currentSectionDetails!: Section;
  readonly currentSectionState!: SectionState;

  readonly getUserAccess!: UserAccess;

  deviceId = 0;

  /*----------  Local data  ----------*/
  isLoading: boolean = true;

  intervalLatestInformation: Array<number> = [];
  metricDataIntervalId = 0;
  /*----------  Vue life-cycles  ----------*/

  async created() {

    await this.GET_SECTION_INFORMATION();

    setTimeout(() => {

      // Überprüfe, ob das DOM-Element existiert, bevor die Klasse hinzugefügt wird
      const tabElement = document.getElementById('informationTab');
      if (tabElement) {
        tabElement.classList.add('active');
      }

    }, 2000)


  }


  /*----------  computed  ----------*/
  get haveSectionIdAndDevicePosition() {
    return this.sectionId !== 0 && this.boardPosition !== "";
  }

  /*----------  Methods  ----------*/

  loadSectionDetails() {
    return this.vwaDeviceMode
      ? this.getVwaSectionDetail(this.sectionId)
      : this.getSectionDetailsFromBackEnd(this.sectionId);
  }

  loadSectionState() {
    return this.vwaDeviceMode ? this.getVwaSectionState(this.sectionId) : this.GetCurrentSectionState(this.sectionId);
  }

  async GET_SECTION_INFORMATION() {
    this.isLoading = true;
    if (!this.isDashboardModal && !this.vwaDeviceMode) this.refreshSectionAllSections(this.currentProject.id);

    if (this.haveSectionIdAndDevicePosition) {
      let getSecInfo = await this.loadSectionDetails();
      let sectionState = await this.loadSectionState();
      if (this.currentSectionDetails.isMeasure) {
        await this.GET_LATEST_COUNTER({
          sectionId: this.currentSectionDetails.id,
          projectVolume: this.currentProject.volumeAnalysisVariant?.name
        });
      }

      if (getSecInfo?.status === 200 || sectionState?.status === 200) {
        this.deviceId = this.boardPosition === "deviceOne" ? getSecInfo.data.boardOne : getSecInfo.data.boardTwo; // Show something to the user that he is view in the latest information in the modal
        this.isLoading = false;

        if(this.currentSectionDetails.type && this.currentSectionDetails.type.name == 'vwa') {
          this.getUserAccess.Access.VIEW.project.vwa.settings = true;
        } else {
          this.getUserAccess.Access.VIEW.project.vwa.settings = false;
        }
      }
    }
  }

  async refreshSectionInformation() {
    const shouldReturn =
      this.currentProject.isArchived ||
      !this.haveSectionIdAndDevicePosition ||
      this.intervalLatestInformation.length > 1;

    if (shouldReturn) return;

    let getStateEvery = 10 * 1000;
    let newInterval = setInterval(async () => {
      this.isLoading = true;
      await this.loadSectionDetails();
      await this.loadSectionState();
      if (this.currentSectionDetails.isMeasure) {
        await this.GET_LATEST_COUNTER({
          sectionId: this.sectionId,
          projectVolume: this.currentProject.volumeAnalysisVariant?.name
        });
      }
      this.isLoading = false;
    }, getStateEvery);

    this.intervalLatestInformation.push(newInterval);
  }

  async clearInterval() {
    let promises = this.intervalLatestInformation.map(async (intervalId: number) => {
      clearInterval(intervalId);
    });
    await Promise.all(promises);
    this.intervalLatestInformation = [];
  }

  async handleModalClose() {
    this.RESET_INFORMATION_MODAL();
    this.RESET_CAMERA_PICTURE();
    this.$emit("infoModalClose");
    await this.clearInterval();
  }

  checkCurrentTab(index: number) {
    this.loadSectionDetails();
    if (index === 0) {
      this.refreshSectionInformation();
    } else {
      this.clearInterval();
    }
    index !== 4 && this.metricDataIntervalId
      ? (clearInterval(this.metricDataIntervalId), (this.metricDataIntervalId = 0))
      : null;
  }

  refreshSchematicView() {
    this.$emit("refresh-schematic-view");
  }
  HandleNewIntervalForMetricData(intervalId: number) {
    this.metricDataIntervalId = intervalId;
  }

  get showApiTab() {
    return (
      this.currentProject.apiExportActive &&
      this.currentSectionDetails.type &&
      this.currentSectionDetails.type.name === "measure"
    );
  }

  get showIOTab() {
    return (
      !this.vwaDeviceMode &&
      this.getUserAccess.Access.VIEW.project.informationModal.administration &&
      this.getUserAccess.Access.VIEW.project.vwa.settings &&
      this.currentSectionState.vwaSettings
    );
  }
}
